/**
 * Alert component for messages and notifications
 */

import { type HTMLAttributes, type ReactNode } from "react";
import styles from "./Alert.module.css";

/**
 * Alert variants
 */
export type AlertVariant = "info" | "success" | "warning" | "error";

/**
 * Alert component props
 */
export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  /** Alert variant */
  variant?: AlertVariant;
  /** Alert title */
  title?: string;
  /** Alert content */
  children: ReactNode;
  /** Dismissible alert */
  dismissible?: boolean;
  /** Dismiss callback */
  onDismiss?: () => void;
}

/**
 * Alert component with accessibility support
 */
export function Alert({
  variant = "info",
  title,
  children,
  dismissible = false,
  onDismiss,
  className,
  ...props
}: AlertProps): React.ReactElement {
  const classNames = [styles.alert, styles[variant], className ?? ""]
    .filter(Boolean)
    .join(" ");

  const role = variant === "error" ? "alert" : "status";

  return (
    <div className={classNames} role={role} aria-live="polite" {...props}>
      <div className={styles.content}>
        {title && <strong className={styles.title}>{title}</strong>}
        <div className={styles.message}>{children}</div>
      </div>
      {dismissible && (
        <button
          type="button"
          className={styles.dismissButton}
          onClick={onDismiss}
          aria-label="Cerrar alerta"
        >
          <span aria-hidden="true">×</span>
        </button>
      )}
    </div>
  );
}

export default Alert;
